// Group real vault agents by their scope. The dashboard collapses every
// scope onto a single Cere accent (see `vault-tokens.css`), so this module
// only attaches a stable icon per scope name — colour decisions live in CSS.

import type { AgentConnection, Scope } from '@cef-ai/vault-sdk';
import GridViewIcon from '@mui/icons-material/GridView';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import type { SectionIcon } from './sections';

export interface GroupedScope {
  id: string;
  label: string;
  Icon: SectionIcon;
  blurb: string;
  agents: AgentConnection[];
}

const SCOPE_ICONS: Record<string, SectionIcon> = {
  conversation: AutoAwesomeIcon,
  productivity: GridViewIcon,
  health:       HomeIcon,
};

const FALLBACK_ICONS: ReadonlyArray<SectionIcon> = [AutoAwesomeIcon, GridViewIcon, SafetyCheckIcon, HomeIcon, SmartToyIcon];

function iconFor(name: string): SectionIcon {
  const known = SCOPE_ICONS[name.toLowerCase()];
  if (known) return known;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % FALLBACK_ICONS.length;
  return FALLBACK_ICONS[h]!;
}

/**
 * Group agents by their `scope` field. Returns one entry per non-empty
 * scope by default, in the order scopes appear in the `scopes` argument;
 * agents whose scope is missing from `scopes` get appended in discovery order.
 * Pass `includeEmpty: true` to also return scopes with zero attached agents
 * (their `agents` arrays will be empty).
 */
export function groupAgentsByScope(
  agents: ReadonlyArray<AgentConnection>,
  scopes: ReadonlyArray<Scope>,
  options: { includeEmpty?: boolean } = {},
): GroupedScope[] {
  const { includeEmpty = false } = options;
  const buckets = new Map<string, AgentConnection[]>();
  for (const a of agents) {
    const arr = buckets.get(a.scope) ?? [];
    arr.push(a);
    buckets.set(a.scope, arr);
  }

  const out: GroupedScope[] = [];
  const seen = new Set<string>();

  for (const s of scopes) {
    const list = buckets.get(s.name) ?? [];
    if (list.length === 0 && !includeEmpty) continue;
    const label = s.displayName?.trim() || s.name;
    out.push({
      id: s.name,
      label,
      Icon: iconFor(s.name),
      blurb: s.streamLabel?.trim() || `Agents writing to the ${label} scope.`,
      agents: list,
    });
    seen.add(s.name);
  }

  for (const [name, list] of buckets) {
    if (seen.has(name) || list.length === 0) continue;
    out.push({
      id: name,
      label: name,
      Icon: iconFor(name),
      blurb: `Agents writing to the ${name} scope.`,
      agents: list,
    });
  }

  return out;
}

/**
 * Per-agent scope label (`agentId -> display label`), derived from the same
 * grouping. Shared by the home blocks and the sidebar so both show the same
 * scope tag without duplicating the labeling walk.
 */
export function scopeLabelsByAgent(
  agents: ReadonlyArray<AgentConnection>,
  scopes: ReadonlyArray<Scope>,
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const g of groupAgentsByScope(agents, scopes, { includeEmpty: true })) {
    for (const a of g.agents) labels.set(a.agentId, g.label);
  }
  return labels;
}
